import React, { useEffect, useState, useCallback, useRef } from "react";
import { useAuth } from "../context/AuthContext";
import styles from "../views/HalalVSHaram.module.css"; // Corrected path
import useJourneyLogic from "../hooks/useJourneyLogic"; // Corrected path
import LoadingSpinner from "../components/LoadingSpinner"; // Corrected path
import { shuffleArray } from "../utils/helpers"; // Corrected path
import { usePreloadedData } from "../context/PreloadContext"; // Corrected path

function HalalvsharamJourneyView({ onComplete, gameLength = 2 }) {
    const { user } = useAuth();
    const autoAdvanceTimeoutRef = useRef(null);

    // --- 1. STATE & HOOKS (UNCONDITIONAL) ---
    const [selectedOption, setSelectedOption] = useState(null);
    const [localScore, setLocalScore] = useState(0); // Using localScore instead of 'score'
    const [showExplanation, setShowExplanation] = useState(false);
    const [timer, setTimer] = useState(30);
    const [isCorrect, setIsCorrect] = useState(false);
    const [showInstructions, setShowInstructions] = useState(true);
    const [shuffledOptions, setShuffledOptions] = useState([]);
    const [done, setDone] = useState(false); // Local state to trigger end

    const ACTIVITY_TYPE = "halalvsharam";
    const { preloadedData } = usePreloadedData();

    // Use the streamlined useJourneyLogic. Removed game state management and popups.
    const {
        questions,
        current,
        setCurrent,
        loadingQuestions,
        error,
        loadQuizQuestions,
        isGameEnded,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    } = useJourneyLogic({
        activityType: ACTIVITY_TYPE,
        gameSpecificEndCondition: done,
        gameLength,
        onComplete,
    });

    const normalizeString = (str) => (typeof str === 'string' ? str.trim().toLowerCase() : '');

    // --- 2. CALLBACKS (UNCONDITIONAL) ---

    // Start quiz
    const startQuiz = useCallback(() => {
        setShowInstructions(false);
        setTimer(30);
    }, []);

    // Next question/End game
    const handleAdvance = useCallback(() => {
        setSelectedOption(null);
        setShowExplanation(false);
        setTimer(30);
        setIsCorrect(false);

        if (current + 1 < questions.length) {
            setCurrent(prev => prev + 1);
        } else {
            setDone(true); // Signal completion
        }
    }, [current, questions.length, setCurrent]);

    // Timeout logic
    const handleTimeout = useCallback(() => {
        setSelectedOption(null);
        setShowExplanation(true);
        setIsCorrect(false);
        // Simplified: only track the attempt, no lives involved
        handleIncorrectAttempt(questions[current]?._id);
    }, [handleIncorrectAttempt, questions, current]);

    // Select option
    const handleSelect = useCallback((option) => {
        setSelectedOption(option);
    }, []);

    // Submit answer
    const handleSubmit = useCallback(async () => {
        if (selectedOption === null) return;
        setShowExplanation(true);

        const currentQuestion = questions[current];
        if (!currentQuestion) return;

        const isCorrectAnswer = normalizeString(selectedOption) === normalizeString(currentQuestion.correctAnswerText);
        setIsCorrect(isCorrectAnswer);

        if (isCorrectAnswer) {
            setLocalScore(prev => prev + 1);
            handleCorrectAttempt(currentQuestion._id);
        } else {
            handleIncorrectAttempt(currentQuestion._id);
        }

        // Removed: axios call to backendApiUrl for progress tracking
    }, [
        selectedOption,
        current,
        questions,
        handleCorrectAttempt,
        handleIncorrectAttempt,
    ]);

    // --- 3. EFFECTS (UNCONDITIONAL) ---

    // --- Data Loading and Initialization ---
    useEffect(() => {
        if (!user) return;
        // resetGameLogicStates is handled internally by the hook now
        if (preloadedData.halalvsharam) {
            loadQuizQuestions(preloadedData.halalvsharam);
        } else {
            loadQuizQuestions();
        }
    }, [user, loadQuizQuestions, preloadedData]);

    // --- Shuffle options when question changes ---
    useEffect(() => {
        if (questions.length > 0 && current < questions.length) {
            // Options are fixed as Halal/Haram, so shuffling is mostly for appearance
            setShuffledOptions(shuffleArray(questions[current].options));
        }
    }, [questions, current]);

    // Timer effect
    useEffect(() => {
        if (
            showExplanation ||
            done ||
            loadingQuestions ||
            questions.length === 0 ||
            showInstructions
        ) return;

        const interval = setInterval(() => {
            setTimer(prev => {
                if (prev === 1) {
                    clearInterval(interval);
                    handleTimeout();
                    return 30;
                }
                return prev - 1;
            });
        }, 1000);
        return () => clearInterval(interval);
    }, [
        current,
        showExplanation,
        done,
        loadingQuestions,
        questions.length,
        showInstructions,
        handleTimeout,
    ]);

    // Auto advance after explanation
    useEffect(() => {
        if (!showExplanation) return;
        autoAdvanceTimeoutRef.current = setTimeout(() => {
            handleAdvance();
        }, 8000);
        return () => clearTimeout(autoAdvanceTimeoutRef.current);
    }, [showExplanation, handleAdvance]);

    // --- 4. CONDITIONAL RENDERING (After all hooks) ---

    // CRITICAL: If the game is ended (by the hook), return null immediately.
    if (isGameEnded) {
        return null;
    }

    // --- Utility Function ---
    const getOptionClass = (option) => {
        if (!questions || questions.length === 0 || current >= questions.length) {
            return styles.optionButton;
        }

        const normSel = normalizeString(selectedOption);
        const normOpt = normalizeString(option);
        const normAns = normalizeString(questions[current].correctAnswerText);

        if (showExplanation) {
            if (normOpt === normAns) return `${styles.optionButton} ${styles.correct}`;
            if (normOpt === normSel) return `${styles.optionButton} ${styles.incorrect}`;
            return styles.optionButton;
        }
        return normOpt === normSel ? `${styles.optionButton} ${styles.selected}` : styles.optionButton;
    };

    // --- Render Logic ---
    const backgroundStyle = { backgroundImage: `url("/images/halal.jpg")` };

    if (!user)
        return (
            <p className={styles.message}>Please login to access the Halal vs Haram activity.</p>
        );

    if (loadingQuestions || error || questions.length === 0) {
        return (
            <div className={styles.background} style={backgroundStyle}>
                <div className={styles.overlay}></div>
                <div className={styles.quizContainer}>
                    {loadingQuestions && <LoadingSpinner />}
                    {error && (
                        <>
                            <p className={styles.errorMessage}>Error: {error}</p>
                            <button onClick={loadQuizQuestions} className={styles.proceedButton}>
                                Retry Loading Questions
                            </button>
                        </>
                    )}
                    {questions.length === 0 && !loadingQuestions && (
                        <p className={styles.message}>
                            No questions found. Please check question data.
                        </p>
                    )}
                </div>
            </div>
        );
    }

    const currentQuestion = questions[current];
    const progressPercent = ((current + 1) / questions.length) * 100;

    return (
        <div className={styles.background} style={backgroundStyle}>
            <div className={styles.overlay}></div>
            <div className={styles.quizContainer}>
                <h2 className={styles.title}>⚖️ Journey Activity: Halal vs Haram</h2>

                {/* --- Instructions --- */}
                {showInstructions ? (
                    <div className={styles.instructionsPopup}>
                        <h3>Journey Mode Instructions:</h3>
                        <ul>
                            <li>Decide if the action on the card is **Halal** (Permissible) or **Haram** (Forbidden).</li>
                            <li>Your goal is completion to advance in your daily journey.</li>
                        </ul>
                        <button className={styles.proceedButton} onClick={startQuiz}>
                            Start Activity
                        </button>
                    </div>
                ) : (
                    <>
                        {/* --- Main Activity UI --- */}
                        <p className={styles.progressText}>
                            Question {current + 1} of {questions.length} (Score: {localScore})
                        </p>
                        <div className={styles.progressWrapper}>
                            <div
                                className={styles.progressBar}
                                style={{ width: `${progressPercent}%` }}
                            />
                        </div>

                        {/* Removed Lives/Hearts Container */}

                        {!showExplanation && <div className={styles.timer}>⏱ {timer}s</div>}

                        <h3 className={styles.question}>{currentQuestion.question}</h3>

                        <ul className={styles.options}>
                            {shuffledOptions.map((option, i) => (
                                <li key={i}>
                                    <button
                                        className={getOptionClass(option)}
                                        onClick={() => handleSelect(option)}
                                        disabled={showExplanation}
                                    >
                                        {option}
                                    </button>
                                </li>
                            ))}
                        </ul>

                        {!showExplanation && (
                            <button
                                className={styles.submit}
                                onClick={handleSubmit}
                                disabled={selectedOption === null}
                            >
                                Submit
                            </button>
                        )}

                        {/* --- Explanation --- */}
                        {showExplanation && (
                            <div className={styles.explanationPopup}>
                                <p className={isCorrect ? styles.correctText : styles.incorrectText}>
                                    {isCorrect ? '✅ Correct, MashaAllah!' : '❌ Incorrect.'}
                                </p>
                                <p>
                                    <strong>Explanation:</strong> {currentQuestion.explanation}
                                </p>
                                <p className={styles.reference}>
                                    <strong>Reference:</strong> {currentQuestion.reference}
                                </p>
                                <button onClick={handleAdvance} className={styles.nextButton}>
                                    {current + 1 < questions.length
                                        ? 'Next Question'
                                        : 'Complete Activity'}
                                </button>
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Removed Popups and Feedback Modal */}
        </div>
    );
}

export default HalalvsharamJourneyView;
